/**
 * Transfer Request service
 */
import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, interval, Observable } from 'rxjs';
import {
  CELL_FILTER,
  CELL_RENDERER,
  COLUMN_SELECTOR_ID,
  ENTITY_TYPES,
  FilterItemsSourceEnum,
  FilterOptionType,
  IApiListResponse,
  IBaseEntityType,
  IDocumentOperationOptions,
  IEntityConfig,
  IFilterQuery,
  ITransfer,
  IXVIListColumn,
} from '@shared/types';

import { getSingularEntityName } from '@app/shared/configs/entity-name-config';
import {
  assign,
  forEach,
  get,
  isBoolean,
  isEmpty,
  isEqual,
  merge,
  set,
  size,
  some,
  unset,
} from 'lodash';
import { detectMobile } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';
import { TransferService } from '../transfer/transfer.service';

import { transferServiceConfig } from '../transfer/transfer.service.config';
import { OPEN_AND_PENDING_STATUS } from '../transfer/transfer-constants';
import { transferTypeFormatter } from '../transfer/transfer.util';
import {
  TRANSFER_REQUEST_STATUS_OPTIONS,
  TRANSFER_STATUS_VALUE,
} from '@app/shared/types/enums/transfer-status.enum';
import { filterTransferRequestParamsParser } from '../filtered-order/filtered.util';
import { ValueFormatterParams } from 'ag-grid-community/dist/lib/entities/colDef';
import { XUXListContainerComponent } from '@app/containers/base/list/xux-list.container.component';
import { rx } from '@app/shared/rx';
import { getTimeAtColDef } from '../transfer/transfer-column.definition';
import { dataSourceFormatter } from '@app/shared/formatters/data-source.formatter';
import { ValueGetterParams } from 'ag-grid-community';
import { lazyInject } from '@containers/base/decorators';
import { ToolsActions } from '@store/actions';
import { fromPromise } from 'rxjs/internal-compatibility';
import { toDate } from '@shared/utils';
import { I18n } from '@app/i18n/strings-keys';

const entityType: ENTITY_TYPES = ENTITY_TYPES.TRANSFER_REQUEST;
const transferRequestServiceConfig: IEntityConfig = merge(
  {},
  transferServiceConfig,
  <IEntityConfig>{
    ENTITY_TYPE: entityType,
    entityNameToCheckPermissions: ENTITY_TYPES.TRANSFER,
    uiRoute: entityType,
    isMasterListAvailable: true,
    buttons: {},
    editor: {
      enableEntryDuplicate: false,
      entityTitle: getSingularEntityName(entityType),
    },
    apiEndpoint: ENTITY_TYPES.TRANSFER,
    isFormServiceSupported: false,
    list: {
      gridOptions: {
        valueField: '_id',
        multiselect: true,
        showPagination: false,
        showViewSelector: false,
        defaultPageSize: 10000,
      },
      defaultViewFieldsSetting: {
        filterQuerySetting: {
          fieldsParser: filterTransferRequestParamsParser,
        },
      },
      documentQuery: {
        orderby: 'send_at asc',
        notNeedActive: true,
        select: [
          'site_id',
          'updated_items',
          'unavailable_items',
          'status',
          'transfer_type',
        ],
      },
      columns: [],
    },
  },
);

/**
 * Transfer Request service that extends TransferService to see
 * Transfer Request from POS
 */
@Injectable()
export class TransferRequestService extends TransferService {
  @lazyInject(ToolsActions) public toolActions: ToolsActions;
  public static config: IEntityConfig = transferRequestServiceConfig;
  public static ENTITY_TYPE: string = transferRequestServiceConfig.ENTITY_TYPE;
  public xuxListContainer: XUXListContainerComponent;

  public beforeStartListMethods: Function[] = [
    this.hookXuxListContainer,
    this.initSubscriptions,
  ];
  public beforeDestroyListMethods: Function[] = [this.cleanupSubscriptions];
  private serverTimestampUTC: Date = toDate();

  public cleanupSubscriptions(): void {
    forEach(this.subscriptions, (subscription) => subscription.unsubscribe());
  }

  public constructor(public injector: Injector) {
    super(injector);

    set(
      this.config,
      'list.titleTooltipIconConfig$',
      new BehaviorSubject({
        tooltipConfig: {
          tooltip: this.i18nService.translate(
            I18n.InfoMessages.TransferRequestAutoRefreshTooltip,
            {
              // every 20 seconds
              count: 20,
            },
          ),
        },
      }),
    );

    this.config.buttons.create = {
      hidden: true,
    };
    this.config.buttons.auto_restock = {
      hidden: true,
    };
    this.config.buttons.print_sheets = {
      hidden: true,
    };
    if (!isBoolean(this.config.buttons.bulk_cancel)) {
      set(
        this.config.buttons.bulk_cancel as any,
        'hidden',
        this.getIsHiddenStatusChangeBtnFn(TRANSFER_STATUS_VALUE.Open).bind(
          this,
        ),
      );
    }

    const minutes = this.stateService.getPendingRequestTimeout();

    const listColumns: IXVIListColumn[] = [
      {
        path: 'process_color',
        title: '',
        defaultView: true,
        minWidth: 40,
        maxWidth: 40,
        headerComponentParams: {
          hintOptions: {
            description:
              minutes === null
                ? this.i18nService.translate(
                    I18n.InfoMessages.GreenStatusTooltip,
                  )
                : this.i18nService.translate(
                    I18n.InfoMessages.UnprocessedStatusTooltip,
                    { minutes },
                  ),
          },
        },
        valueGetter: (params: ValueGetterParams) =>
          get(params, 'data.process_color'),
        cellRenderer: CELL_RENDERER.bell_renderer,
      },
      {
        path: 'transaction_date',
        title: I18n.Common.Date,
        filterType: CELL_FILTER.dateLocale,
        sortable: true,
        defaultView: true,
      },
      {
        path: 'internal_number',
        title: I18n.Forms.GridForm.ID,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        sortable: true,
        defaultView: true,
      },
      {
        path: 'source',
        title: I18n.Common.Source,
        width: 110,
        defaultView: true,
        valueFormatter: (params) =>
          dataSourceFormatter(params, { i18nService: this.i18nService }),
      },
      {
        path: 'site_name',
        title: I18n.Forms.TransferForm.FromName,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        cellRenderer: CELL_RENDERER.link_renderer,
        cellRendererParams: {
          entityType,
          needGetUpdateItemsStatus: OPEN_AND_PENDING_STATUS,
        },
        sortable: true,
        defaultView: true,
      },
      {
        path: 'store_number',
        title: I18n.Forms.TransferForm.FromID,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        cellRenderer: CELL_RENDERER.link_renderer,
        cellRendererParams: {
          entityType,
          needGetUpdateItemsStatus: OPEN_AND_PENDING_STATUS,
        },
        sortable: true,
      },
      {
        path: 'site_name_to',
        title: I18n.Forms.TransferForm.ToName,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        cellRenderer: CELL_RENDERER.link_renderer,
        cellRendererParams: {
          entityType,
          needGetUpdateItemsStatus: OPEN_AND_PENDING_STATUS,
        },
        sortable: true,
        defaultView: true,
      },
      {
        path: 'store_number_to',
        title: I18n.Forms.TransferForm.ToID,
        filterType: 'stringFilter',
        filterParams: {
          quickFilter: true,
        },
        cellRenderer: CELL_RENDERER.link_renderer,
        cellRendererParams: {
          entityType,
          needGetUpdateItemsStatus: OPEN_AND_PENDING_STATUS,
        },
        sortable: true,
      },
      {
        path: 'transfer_type',
        title: I18n.Common.Type,
        valueFormatter: (params) =>
          transferTypeFormatter(params, { i18nService: this.i18nService }),
        defaultView: false,
      },
      {
        path: 'event_id',
        title: I18n.EntityNames.Event,
        defaultView: true,
        filterType: CELL_FILTER.entity,
        filterParams: {
          default: {
            type: 'containsAny' as FilterOptionType,
          },
          filterItemsSource: FilterItemsSourceEnum.XPRT_EVENTS,
          filterOptions: ['containsAny'] as FilterOptionType[],
          filterItemSourceQuery: {
            select: ['name', 'id'],
          },
          suppressUIFiltering: true,
        },
      },
      {
        path: 'is_send',
        title: I18n.Common.Status,
        valueFormatter: (params: ValueFormatterParams): string =>
          this.i18nService.translate(
            params.value ? I18n.Status.Open : I18n.Status.OpenDraftBrackets,
          ),
        sortable: true,
        defaultView: true,
        filterType: CELL_FILTER.enum,
        filterParams: {
          values: TRANSFER_REQUEST_STATUS_OPTIONS,
        },
      },
      getTimeAtColDef({
        companyService: this.companyService,
        i18nService: this.i18nService,
      }),
      {
        ...getTimeAtColDef({
          companyService: this.companyService,
          i18nService: this.i18nService,
          path: 'send_at',
          title: I18n.Common.TimeSent,
        }),
      },
      {
        path: COLUMN_SELECTOR_ID,
        width: 50,
        title: '',
        defaultView: true,
        cellRenderer: CELL_RENDERER.transfer_request_actions_render,
        pinned: detectMobile() ? false : 'right',
        lockPinned: true,
      },
    ];

    this.config.list.columns = [...listColumns];
  }

  public fetchList(
    userQuery?: IFilterQuery,
    options: IDocumentOperationOptions = {},
    skipRequestEvent: boolean = false,
    isTrowError: boolean = false,
  ): Observable<any> {
    return fromPromise(this.toolActions.fetchInfo()).pipe(
      rx.switchMap((res) => {
        this.serverTimestampUTC = toDate(
          get(res, 'serverTimestampUTC', Date.now()),
        );

        return super.fetchList(
          userQuery,
          options,
          skipRequestEvent,
          isTrowError,
        );
      }),
    );
  }

  public groupByStatus(
    apiResponse: IApiListResponse,
  ): IApiListResponse<IBaseEntityType> {
    const timeout = this.stateService.getPendingRequestTimeout();
    const red: IBaseEntityType[] = [];
    const green: IBaseEntityType[] = [];
    const edited: IBaseEntityType[] = [];
    const draft: IBaseEntityType[] = [];

    forEach(get(apiResponse, 'items'), (item: IBaseEntityType) => {
      if (!get(item, 'is_send')) {
        return draft.push(item);
      }
      const updatedAt = toDate(get(item, 'updated_at', 0));
      const sendAt = toDate(get(item, 'send_at', 0));

      if (updatedAt > sendAt) {
        return edited.push(item);
      }

      if (timeout === null) {
        set(item, 'process_color', 'green');

        return green.push(item);
      }

      const diffMs = this.serverTimestampUTC.getTime() - sendAt.getTime();

      if (diffMs < timeout * 60 * 1000) {
        set(item, 'process_color', 'green');
        green.push(item);
      } else {
        set(item, 'process_color', 'red');
        red.push(item);
      }
    });

    return assign(apiResponse, {
      items: [...red, ...green, ...edited, ...draft],
    });
  }

  public backToFrontMapper(
    apiResponse: IApiListResponse,
    options: IDocumentOperationOptions = {},
  ): any {
    return this.groupByStatus(super.backToFrontMapper(apiResponse, options));
  }

  public frontToBackItemMapper(doc: ITransfer): IBaseEntityType {
    unset(doc, 'process_color');

    return super.frontToBackItemMapper(doc);
  }

  public hookXuxListContainer(
    xuxListContainerComponent: XUXListContainerComponent,
  ): void {
    this.xuxListContainer = xuxListContainerComponent;
  }

  public initSubscriptions() {
    this.autoRefreshFetchTransferDataStub();
  }

  public hasNoSelectedRow(): boolean {
    return isEmpty(this.xuxListContainer.gridApi.getSelectedRows());
  }

  public autoRefreshFetchTransferDataStub(): void {
    this.subscriptions.push(
      interval(20000)
        .pipe(rx.filter(() => this.hasNoSelectedRow()))
        .subscribe(() => {
          this.xuxListContainer.refresh();
        }),
    );
  }

  public get isCanManage(): boolean {
    return this.permissionService.canManage(TransferService.ENTITY_TYPE);
  }

  public getIsHiddenStatusChangeBtnFn(
    targetStatus?: TRANSFER_STATUS_VALUE,
    isSingleSelection: boolean = false,
  ): Function {
    return (selectedItems: IBaseEntityType[]): boolean => {
      const isNotOnlyTargetStatusItems = (): boolean =>
        isEmpty(targetStatus)
          ? false
          : some(selectedItems, (item) => !isEqual(targetStatus, item.status));

      const selectedItemLength = size(selectedItems);
      const isSelectedItemInvalid = isSingleSelection
        ? selectedItemLength !== 1
        : selectedItemLength < 1;

      return (
        !this.isCanManage ||
        isSelectedItemInvalid ||
        isNotOnlyTargetStatusItems()
      );
    };
  }
}
